import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { useDeviceSize } from '@vmos2/providers';

import { Image } from '../../image/Image';
import { Title } from '../../titles/Title';
import { useComponentTheme } from '../../useComponentTheme';

type Props = {
  title: string,
  description?: string,
  uuid: string,
  headerRef?: Function,
  children?: React.Node,
  placeholder?: string,
  imageSrc?: string,
  externalSrc?: string,
  mode?: string,
  isLarge?: boolean,
  hasMobileSquareImage?: boolean,
};

export const Sidebar = ({
  title,
  description,
  uuid,
  headerRef,
  children,
  placeholder,
  imageSrc,
  externalSrc,
  mode,
  isLarge,
  hasMobileSquareImage,
}: Props) => {
  const { isMobile } = useDeviceSize();
  const theme = useTheme();
  const componentTheme = useComponentTheme(theme => theme.components.Sidebar, mode);
  const color = componentTheme.mode === 'dark' && isLarge ? theme.color.white : theme.color.black;

  return (
    <Aside componentTheme={componentTheme} isLarge={isLarge} color={color}>
      <ImageWrapper
        isSquare={hasMobileSquareImage}
        uuid={uuid}
        alt={title}
        placeholder={placeholder}
        src={imageSrc}
        externalSrc={externalSrc}
        sizes={isMobile ? '100vw' : '33vw'}
        mode="square"
      />
      <Separator ref={headerRef} />
      <Content>
        <Title level={1} color={color}>
          <TitleText>{title}</TitleText>
        </Title>
        <Description dangerouslySetInnerHTML={{ __html: description }} />
        {children}
      </Content>
    </Aside>
  );
};

const Aside = styled('div')`
  ${({ isLarge }) =>
    isLarge &&
    `
      padding: 0;
      background: ${({ theme }) => theme.color.white};
      color: ${({ theme }) => theme.color.black};
  `}

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    ${({ isLarge, componentTheme, color }) =>
      isLarge &&
      `
      padding: 32px;
      background-color: ${componentTheme.backgroundColor};
      min-height: 100%;
      color: ${color};
    `}
  }
`;

const ImageWrapper = styled(Image, { shouldForwardProp: prop => prop !== 'isSquare' })`
  margin: 0 -24px;
  border-radius: 0;

  ${({ isSquare, theme }) =>
    !isSquare &&
    `
    height: 176px;
    padding-top: 0;

    @media (${theme.breakpoint.tablet}) {
      height: 300px;
    }
  `}

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    height: auto;
    margin: 0 0 32px;
    padding-top: 100%;
    border-radius: ${({ theme }) => theme.borderRadius.large};
  }
`;

const Separator = styled('hr')`
  visibility: hidden;
  margin: 0;
  border: 0;
`;

const Content = styled('div')`
  margin-top: 24px;
  overflow-wrap: break-word;

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    margin-top: 0;
  }
`;

const Description = styled('div')`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  margin: 0 0 16px;
`;

const TitleText = styled('span')`
  overflow-wrap: break-word;
`;
